import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Siren,
  Clock,
  Activity,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Wrench,
  Eye,
  Copy,
  FileText,
  Loader2,
} from 'lucide-react';
import { PageBody } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { PropertyChips } from '@/components/ui/Tabs';
import { Term } from '@/components/ui/Term';
import { Tooltip } from '@/components/ui/Tooltip';
import { Modal } from '@/components/ui/Modal';
import {
  useIncidentDetail,
  useAppendIncidentUpdate,
  useResolveIncident,
  usePatchIncident,
} from '@/api/incidents';
import { useTenants } from '@/api/tenants';
import { toast } from '@/components/ui/Toaster';
import { REGION_LABEL } from '@/lib/tier';
import type { IncidentSeverity, IncidentStatus, IncidentUpdateKind } from '@/data/types';
import { cn } from '@/lib/cn';

const POSTMORTEM_TEMPLATE = `## What happened

## Impact
- Customers affected:
- Duration:

## Root cause

## Timeline (UTC)
-

## What went well

## What went badly

## Action items
- [ ] `;

const SEV_TONE: Record<IncidentSeverity, 'neutral' | 'accent' | 'success' | 'warn' | 'danger'> = {
  sev1: 'danger',
  sev2: 'warn',
  sev3: 'accent',
};

const STATUS_TONE: Record<IncidentStatus, 'neutral' | 'accent' | 'success' | 'warn' | 'danger'> = {
  investigating: 'warn',
  identified: 'accent',
  monitoring: 'accent',
  resolved: 'success',
};

const KIND_ICON: Record<IncidentUpdateKind, React.ReactNode> = {
  opened: <Siren size={11} />,
  update: <Activity size={11} />,
  identified: <AlertCircle size={11} />,
  mitigation: <Wrench size={11} />,
  monitoring: <Eye size={11} />,
  resolved: <CheckCircle2 size={11} />,
};

const KIND_TONE: Record<IncidentUpdateKind, string> = {
  opened: 'bg-[color:var(--danger-soft)] text-[color:var(--danger)]',
  update: 'bg-surface-2 text-fg-muted',
  identified: 'bg-[color:var(--accent-soft)] text-accent',
  mitigation: 'bg-[color:var(--warn-soft)] text-[color:var(--warn)]',
  monitoring: 'bg-[color:var(--accent-soft)] text-accent',
  resolved: 'bg-[color:var(--success-soft)] text-[color:var(--success)]',
};

export function IncidentDetailRoute() {
  const { id } = useParams();
  const { data: apiIncident, isLoading } = useIncidentDetail(id);
  const { data: apiTenants = [] } = useTenants();
  const appendUpdate = useAppendIncidentUpdate();
  const resolveIncident = useResolveIncident();
  const patchIncident = usePatchIncident();
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [editField, setEditField] = useState<'public_summary' | 'postmortem' | null>(null);
  // Adapt API → UI shape on the fly.
  const incident = apiIncident ? {
    id: apiIncident.id,
    title: apiIncident.title,
    severity: apiIncident.severity,
    status: apiIncident.status,
    startedAt: apiIncident.started_at,
    resolvedAt: apiIncident.resolved_at ?? undefined,
    affectedTenants: apiIncident.affected_tenant_ids.length,
    affectedTenantIds: apiIncident.affected_tenant_ids,
    affectedRegion: apiIncident.affected_region ?? undefined,
    oncall: apiIncident.oncall,
    publicSummary: apiIncident.public_summary ?? undefined,
    postmortem: apiIncident.postmortem ?? undefined,
    timeToDetectSec: apiIncident.time_to_detect_sec ?? undefined,
    timeToAckSec: apiIncident.time_to_ack_sec ?? undefined,
    updates: apiIncident.updates,
  } : undefined;
  void isLoading;

  if (!incident) {
    return (
      <PageBody>
        <Card className="p-8 text-center">
          <div className="text-md font-semibold">Incident not found</div>
          <Link to="/incidents" className="text-accent text-sm">← Back to incidents</Link>
        </Card>
      </PageBody>
    );
  }

  const isResolved = incident.status === 'resolved';
  const durationSec = isResolved && incident.resolvedAt
    ? Math.round((new Date(incident.resolvedAt).getTime() - new Date(incident.startedAt).getTime()) / 1000)
    : Math.round((Date.now() - new Date(incident.startedAt).getTime()) / 1000);

  const affectedTenants = (incident.affectedTenantIds ?? [])
    .map(tid => apiTenants.find(t => t.id === tid || t.slug === tid))
    .filter((t): t is NonNullable<typeof t> => Boolean(t));

  const updates = incident.updates ?? [];

  async function doResolve() {
    if (!window.confirm(`Mark "${incident!.title}" resolved? This posts a final update and closes the incident.`)) return;
    try {
      await resolveIncident.mutateAsync(incident!.id);
      toast.success('Incident resolved', incident!.title);
    } catch (e: any) {
      toast.danger('Could not resolve', e?.message ?? 'Unknown error');
    }
  }

  function copyPostmortemTemplate() {
    navigator.clipboard?.writeText(POSTMORTEM_TEMPLATE);
    toast.info('Postmortem template copied to clipboard');
  }

  return (
    <>
      {/* H1 */}
      <div className="px-4 md:px-6 lg:px-8 pt-5 pb-4 bg-bg-elevated border-b border-border">
        <Link to="/incidents" className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg mb-3">
          <ArrowLeft size={14} /> Incidents
        </Link>
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Siren size={18} className="text-[color:var(--warn)]" />
              <h1 className="text-xl md:text-2xl font-semibold tracking-tight">{incident.title}</h1>
              <Badge tone={SEV_TONE[incident.severity]}>{incident.severity.toUpperCase()}</Badge>
              <Badge tone={STATUS_TONE[incident.status]} className="capitalize">{incident.status}</Badge>
              <button
                className="text-fg-subtle hover:text-fg ml-1 inline-flex items-center gap-1 text-2xs font-mono"
                onClick={() => navigator.clipboard?.writeText(incident.id)}
                title="Copy incident ID"
              >
                <Copy size={11} /> {incident.id}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button variant="ghost" size="sm" iconLeft={<FileText size={13} />} onClick={copyPostmortemTemplate}>
              Postmortem template
            </Button>
            {!isResolved && (
              <Button variant="secondary" size="sm" iconLeft={<Activity size={13} />} onClick={() => setUpdateModalOpen(true)}>
                Post update
              </Button>
            )}
            {!isResolved && (
              <Button variant="primary" size="sm" iconLeft={resolveIncident.isPending ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} onClick={doResolve} disabled={resolveIncident.isPending}>
                Mark resolved
              </Button>
            )}
          </div>
        </div>

        <PropertyChips
          chips={[
            { label: 'Started', value: new Date(incident.startedAt).toLocaleString('en-GB', { hour12: false }) },
            { label: isResolved ? 'Resolved' : 'Duration', value: isResolved ? new Date(incident.resolvedAt!).toLocaleString('en-GB', { hour12: false }) : formatDuration(durationSec) },
            { label: 'On-call', value: incident.oncall },
            { label: 'Affected', value: `${incident.affectedTenants} ${incident.affectedTenants === 1 ? 'tenant' : 'tenants'}` },
            ...(incident.affectedRegion ? [{ label: 'Region', value: REGION_LABEL[incident.affectedRegion] }] : []),
          ]}
        />
      </div>

      <PageBody>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-4">

            {incident.publicSummary && (
              <Card>
                <CardHeader
                  title={<><ShieldCheck size={14} className="inline -mt-0.5 mr-1.5 text-accent" />Public status-page summary</>}
                  description="What customers see on nousviz.cloud."
                  actions={<Button variant="ghost" size="sm" onClick={() => setEditField('public_summary')}>Edit</Button>}
                />
                <CardBody>
                  <p className="text-sm text-fg leading-relaxed">{incident.publicSummary}</p>
                </CardBody>
              </Card>
            )}

            {/* Timeline */}
            <Card>
              <CardHeader
                title="Timeline"
                description="Append-only thread. Every update notifies on-call + the customer on the status page (if sev ≥ sev2)."
                actions={
                  !isResolved && (
                    <Button variant="secondary" size="sm" onClick={() => setUpdateModalOpen(true)}>
                      Post update
                    </Button>
                  )
                }
              />
              <CardBody>
                {updates.length === 0 ? (
                  <div className="text-center text-sm text-fg-muted py-6">No updates yet.</div>
                ) : (
                  <ol className="relative">
                    <span className="absolute left-3.5 top-2 bottom-2 w-px bg-border" />
                    {updates.map((u, i) => (
                      <li key={u.id} className="relative pl-10 pb-5 last:pb-0">
                        <span
                          className={cn(
                            'absolute left-1 top-0 w-7 h-7 rounded-full border-[3px] border-bg-elevated flex items-center justify-center shrink-0',
                            KIND_TONE[u.kind],
                          )}
                        >
                          {KIND_ICON[u.kind]}
                        </span>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-semibold text-fg text-sm capitalize">{u.kind}</span>
                          <span className="text-2xs text-fg-subtle">·</span>
                          <span className="text-2xs text-fg-muted">{u.author}</span>
                          <span className="ml-auto text-2xs text-fg-subtle tabnum">
                            {new Date(u.timestamp).toLocaleString('en-GB', { hour12: false })}
                            {i > 0 && <span className="ml-2">+{formatDuration(Math.round((new Date(u.timestamp).getTime() - new Date(updates[0].timestamp).getTime()) / 1000))}</span>}
                          </span>
                        </div>
                        <p className="text-sm text-fg mt-1 leading-relaxed">{u.message}</p>
                      </li>
                    ))}
                  </ol>
                )}
              </CardBody>
            </Card>

            {/* Postmortem */}
            {isResolved && (
              <Card>
                <CardHeader
                  title="Postmortem"
                  description="Root-cause analysis + action items. Required for sev1 and sev2 within 5 working days."
                  actions={
                    <Button variant="secondary" size="sm" iconLeft={<FileText size={13} />} onClick={() => setEditField('postmortem')}>
                      Edit postmortem
                    </Button>
                  }
                />
                <CardBody>
                  {incident.postmortem ? (
                    <p className="text-sm text-fg leading-relaxed whitespace-pre-wrap">{incident.postmortem}</p>
                  ) : (
                    <div className="text-sm text-fg-muted">
                      No postmortem written yet.{' '}
                      <button className="text-accent hover:underline" onClick={() => setEditField('postmortem')}>Start one</button>.
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
          </div>

          <div className="space-y-4">
            {/* Metrics */}
            <Card>
              <CardHeader title="Response metrics" />
              <CardBody className="space-y-3 text-sm">
                <Metric
                  icon={<Clock size={13} />}
                  label="Time to detect"
                  tooltip="From the first symptom to a monitor firing."
                  value={incident.timeToDetectSec ? formatDuration(incident.timeToDetectSec) : '—'}
                />
                <Metric
                  icon={<Clock size={13} />}
                  label="Time to acknowledge"
                  tooltip="From monitor fire to on-call responder paging in."
                  value={incident.timeToAckSec ? formatDuration(incident.timeToAckSec) : '—'}
                />
                <Metric
                  icon={<Clock size={13} />}
                  label={<><Term k="mttr" plain>MTTR</Term> (this incident)</>}
                  tooltip="From open to resolved."
                  value={isResolved ? formatDuration(durationSec) : 'In flight'}
                  highlight={isResolved}
                />
              </CardBody>
            </Card>

            {/* Affected tenants */}
            {affectedTenants.length > 0 && (
              <Card>
                <CardHeader title={`Affected customers (${affectedTenants.length})`} />
                <CardBody className="space-y-1.5">
                  {affectedTenants.map(t => (
                    <Link
                      key={t.id}
                      to={`/tenants/${t.slug}`}
                      className="flex items-center justify-between gap-2 px-2 py-1.5 -mx-2 rounded hover:bg-surface-2 transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-fg truncate">{t.name}</div>
                        <div className="text-2xs text-fg-subtle truncate">{t.owner_email}</div>
                      </div>
                      <ArrowLeft size={12} className="text-fg-subtle rotate-180 shrink-0" />
                    </Link>
                  ))}
                </CardBody>
              </Card>
            )}

            {/* On-call escalation */}
            <Card>
              <CardHeader title="On-call & escalation" />
              <CardBody className="text-sm space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-fg-muted">Primary</span>
                  <span className="text-fg font-medium">{incident.oncall}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-fg-muted">Secondary</span>
                  <span className="text-fg-subtle">Not configured</span>
                </div>
                <div className="pt-2 mt-1 border-t border-border text-xs text-fg-subtle">
                  Escalation chain comes from <Link to="/staff" className="text-accent hover:underline">Staff</Link> in Phase 1.
                </div>
              </CardBody>
            </Card>

            {/* Status-page status */}
            <Card>
              <CardHeader title="Status page" />
              <CardBody className="text-sm space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-fg-muted">Published</span>
                  <Badge tone={incident.severity === 'sev3' ? 'neutral' : 'success'}>
                    {incident.severity === 'sev3' ? 'Internal-only' : 'Public'}
                  </Badge>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-fg-muted">URL</span>
                  <a className="text-accent hover:underline font-mono text-xs" href="#">
                    nousviz.cloud/i/{incident.id}
                  </a>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </PageBody>

      <PostUpdateModal
        open={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
        onSubmit={async (kind, message) => {
          await appendUpdate.mutateAsync({ id: incident.id, kind, message });
          toast.success('Update posted', `${kind} — ${message.slice(0, 60)}`);
        }}
        pending={appendUpdate.isPending}
      />

      <EditTextModal
        open={editField !== null}
        onClose={() => setEditField(null)}
        title={editField === 'postmortem' ? 'Edit postmortem' : 'Edit public summary'}
        description={
          editField === 'postmortem'
            ? 'Root-cause analysis + action items. Markdown is fine.'
            : 'Customer-facing. Plain language, no internal details.'
        }
        initial={
          editField === 'postmortem'
            ? (incident.postmortem || POSTMORTEM_TEMPLATE)
            : (incident.publicSummary || '')
        }
        onSave={async (text) => {
          await patchIncident.mutateAsync(
            editField === 'postmortem'
              ? { id: incident.id, postmortem: text }
              : { id: incident.id, public_summary: text },
          );
          toast.success(editField === 'postmortem' ? 'Postmortem saved' : 'Public summary saved');
        }}
        pending={patchIncident.isPending}
      />
    </>
  );
}

const UPDATE_KINDS: { value: IncidentUpdateKind; label: string }[] = [
  { value: 'update', label: 'Update — progress note' },
  { value: 'identified', label: 'Identified — root cause found' },
  { value: 'mitigation', label: 'Mitigation — fix being applied' },
  { value: 'monitoring', label: 'Monitoring — fix applied, watching' },
  { value: 'resolved', label: 'Resolved — closes the incident' },
];

function PostUpdateModal({
  open, onClose, onSubmit, pending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (kind: IncidentUpdateKind, message: string) => Promise<void>;
  pending: boolean;
}) {
  const [kind, setKind] = useState<IncidentUpdateKind>('update');
  const [message, setMessage] = useState('');
  const canSubmit = message.trim().length > 0 && !pending;

  async function submit() {
    if (!canSubmit) return;
    try {
      await onSubmit(kind, message.trim());
      setMessage('');
      setKind('update');
      onClose();
    } catch (e: any) {
      toast.danger('Could not post update', e?.message ?? 'Unknown error');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!pending) onClose(); }}
      size="md"
      title="Post incident update"
      description="Appends to the timeline. 'Identified', 'monitoring' and 'resolved' also advance the incident status."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {pending ? <><Loader2 size={13} className="animate-spin mr-1.5" />Posting…</> : 'Post update'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">Kind</label>
          <select
            value={kind}
            onChange={e => setKind(e.target.value as IncidentUpdateKind)}
            disabled={pending}
            className="w-full h-8 px-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
          >
            {UPDATE_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Message <span className="text-[color:var(--danger)]">*</span>
          </label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={4}
            autoFocus
            disabled={pending}
            placeholder="What changed since the last update?"
            className="w-full px-2.5 py-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm resize-y"
          />
        </div>
      </div>
    </Modal>
  );
}

function EditTextModal({
  open, onClose, title, description, initial, onSave, pending,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  initial: string;
  onSave: (text: string) => Promise<void>;
  pending: boolean;
}) {
  const [text, setText] = useState(initial);
  // Re-seed when a different field/incident opens.
  const [seededFrom, setSeededFrom] = useState(initial);
  if (open && seededFrom !== initial) {
    setText(initial);
    setSeededFrom(initial);
  }

  async function save() {
    if (pending) return;
    try {
      await onSave(text);
      onClose();
    } catch (e: any) {
      toast.danger('Could not save', e?.message ?? 'Unknown error');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!pending) onClose(); }}
      size="lg"
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={pending}>
            {pending ? <><Loader2 size={13} className="animate-spin mr-1.5" />Saving…</> : 'Save'}
          </Button>
        </>
      }
    >
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={14}
        autoFocus
        disabled={pending}
        className="w-full px-2.5 py-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm font-mono resize-y"
      />
    </Modal>
  );
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function Metric({
  icon,
  label,
  value,
  tooltip,
  highlight,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  value: string;
  tooltip: string;
  highlight?: boolean;
}) {
  return (
    <Tooltip content={tooltip}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-fg-muted inline-flex items-center gap-1.5">
          {icon}
          {label}
        </span>
        <span className={cn('font-semibold tabnum', highlight && 'text-[color:var(--success)]')}>
          {value}
        </span>
      </div>
    </Tooltip>
  );
}
